import { useProductContext } from '@bluheadless/ui-logic/src/providers/product'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { cx } from '@emotion/css'
import dynamic from 'next/dynamic'
import { func, object } from 'prop-types'
import { useState } from 'react'
import Zoom from '../../zoom'
import Typography from '@bluheadless/ui/src/atoms/typography'
import { styled } from '@mui/system'
import { useIntl } from 'react-intl'
import { OutOfStockLabel } from '../sidebar/sidebar-top.styled'

const Mosaic = dynamic(() => import('./mosaic'))
const Slider = dynamic(() => import('./slider'))
const SliderThumbs = dynamic(() => import('./slider-thumbs'))
const VerticalThumbs = dynamic(() => import('./vertical-thumbs'))

const LabelsWrapper = styled('div')`
	position: absolute;
	top: 14px;
	left: 8px;
	display: flex;
	justify-content: flex-start;
	align-items: center;
	z-index: 9;
	gap: var(--spacing-1);

	${({ theme }) => theme.breakpoints.up('md')} {
		top: 18px;
		left: 16px;
	}
`
const ProductLabel = styled(Typography)`
	text-transform: uppercase;
	font-weight: 500;
	padding: 4px 13px;
	border-radius: 100px;
	background: rgba(255, 255, 255, 0.3);
	backdrop-filter: blur(100px)
	border: 1px solid transparent;
`

const GalleryType = ({
	assets,
	mosaicProps,
	setToggleZoom,
	sliderProps,
	sliderThumbsProps,
	verticalThumbsProps,
	toggleZoom,
	type,
	zoomEnabled,
	productType,
}) => {
	switch (type) {
		case 'mosaic':
			return (
				<Mosaic
					productType={productType}
					zoomEnabled={zoomEnabled}
					handlerToggleZoom={setToggleZoom}
					assets={assets}
					{...mosaicProps}
				/>
			)

		case 'vertical-thumbs':
			return (
				<VerticalThumbs
					productType={productType}
					zoomEnabled={zoomEnabled}
					handlerToggleZoom={setToggleZoom}
					assets={assets}
					{...verticalThumbsProps}
				/>
			)

		case 'slider':
			return (
				<Slider
					productType={productType}
					zoomEnabled={zoomEnabled}
					handlerToggleZoom={setToggleZoom}
					currentImage={toggleZoom?.currentImage}
					assets={assets}
					{...sliderProps}
				/>
			)

		case 'slider-thumbs':
			return (
				<SliderThumbs
					productType={productType}
					zoomEnabled={zoomEnabled}
					handlerToggleZoom={setToggleZoom}
					assets={assets}
					currentImage={toggleZoom?.currentImage}
					{...sliderThumbsProps}
				/>
			)
	}
}

const Gallery = ({ className, mosaicProps, sliderProps, sliderThumbsProps, verticalThumbsProps, ...props }) => {
	const productContextValue = useProductContext()
	const { formatMessage } = useIntl()

	const [
		{
			assets,
			product: { type, name, attributes },
			inventory: { inStock },
		},
	] = productContextValue

	const [toggleZoom, setToggleZoom] = useState({
		open: false,
		currentImage: undefined,
	})

	const {
		siteName,
		catalog: {
			products: {
				outOfStockChip,
				gallery,
				zoom: { enabled: zoomEnabled },
				altImgSuffixAttributeCode,
				additionalLabel: { attributeCode },
			},
		},
	} = useConfig()

	const galleryAssets = assets.filter((asset) => !asset.hideFromGallery)

	galleryAssets.map((asset) => {
		if (!asset.alt) {
			asset.alt =
				name +
				(altImgSuffixAttributeCode && attributes?.[altImgSuffixAttributeCode]?.value
					? ' - ' + attributes?.[altImgSuffixAttributeCode]?.value
					: '') +
				' - ' +
				siteName
		}
	})
	const filteredAssetsByImageType = galleryAssets.filter((asset) => asset.type === 'image')

	return galleryAssets?.length > 0 ? (
		<div className={cx('Gallery-root', className)} {...props}>
			<LabelsWrapper>
				{attributes[attributeCode]?.value && <ProductLabel>{attributes[attributeCode]?.value}</ProductLabel>}
				{!inStock && (
					<OutOfStockLabel
						label={formatMessage({ id: 'out_of_stock' })}
						textColor={outOfStockChip?.textColor}
						color={outOfStockChip?.color}
					/>
				)}
			</LabelsWrapper>
			<GalleryType
				assets={galleryAssets}
				mosaicProps={mosaicProps}
				setToggleZoom={setToggleZoom}
				sliderProps={sliderProps}
				sliderThumbsProps={sliderThumbsProps}
				verticalThumbsProps={verticalThumbsProps}
				toggleZoom={toggleZoom}
				type={gallery[type]}
				zoomEnabled={zoomEnabled}
				productType={type}
			/>
			{zoomEnabled && (
				<Zoom
					open={toggleZoom?.open}
					currentImage={toggleZoom?.currentImage}
					setToggleZoom={setToggleZoom}
					images={filteredAssetsByImageType}
					thumbsProps={{ direction: 'vertical', slidesPerView: 5 }}
				/>
			)}
		</div>
	) : null
}

Gallery.propTypes = {
	mosaicProps: object,
	sliderProps: object,
	sliderThumbsProps: object,
	verticalThumbsProps: object,
	stickyRef: func,
}

export default Gallery
